import { RouteRow } from './routeModel';

/**
 * Cloud health of a route: what the reflector actually achieved in AWS, as
 * opposed to what BGP decided (CTX-323 / CTX-328).
 *
 * Two independent signals arrive per (prefix, route table):
 *   - `injectionStatus` — the reflector's own write result (injected / failed
 *     with the AWS error / withdrawn).
 *   - `awsState` — what the reconciliation sweep last observed in AWS
 *     (active / blackhole / missing / drift / table_missing).
 *
 * They can disagree, and that disagreement is the whole point: a route can be
 * BGP-selected, written successfully, and still be a **blackhole** in AWS
 * because the ENI behind it died. That case was invisible before this feature
 * (it caused a 1h21m silent outage) and is now a first-class state.
 *
 * Absence is NOT health: a reflector older than the feature sends neither
 * signal, so those rows resolve to `unknown` and must render as "no data"
 * rather than as a green light.
 */
export type CloudRouteHealthState =
  | 'healthy'
  | 'blackhole'
  | 'failed'
  | 'drift'
  | 'withdrawn'
  | 'unverified'
  | 'unknown';

export interface CloudRouteHealth {
  state: CloudRouteHealthState;
  /** Short badge text. Empty for `unknown` — the caller renders a dash. */
  label: string;
  variant: 'success' | 'error' | 'warning' | 'neutral' | 'default';
  /** One-line explanation for a tooltip; includes the AWS error when there is one. */
  detail: string;
  /**
   * True when the sweep's verdict predates the last write, so `awsState`
   * describes a route object that has since been rewritten. The next sweep
   * pass refreshes it; until then the badge is shown but flagged.
   */
  stale: boolean;
}

/** Epoch seconds -> local string, matching how the table renders its other timestamps. */
const at = (epochSeconds: number | null): string =>
  epochSeconds == null ? 'an unknown time' : new Date(epochSeconds * 1000).toLocaleString();

/**
 * Single source of truth for the badge, the tooltip and any filtering. Every
 * surface (table, detail panel, topology) must call this instead of
 * re-deriving the precedence, or the surfaces will disagree.
 *
 * Precedence, worst-first — a write that failed outranks any stored verdict,
 * and a blackhole outranks a stale "active":
 *   failed > blackhole > drift/missing > withdrawn > active > injected-unverified
 */
export function deriveCloudRouteHealth(row: RouteRow): CloudRouteHealth {
  const { injectionStatus, injectionError, injectionAt, awsState, awsCheckedAt } = row;

  if (injectionStatus == null && awsState == null) {
    return {
      state: 'unknown',
      label: '',
      variant: 'default',
      detail: 'This reflector does not report cloud injection state.',
      stale: false
    };
  }

  const stale =
    awsCheckedAt != null && injectionAt != null && awsCheckedAt < injectionAt;
  const checked = awsState == null ? '' : ` Last checked in AWS at ${at(awsCheckedAt)}.`;
  const staleNote = stale
    ? ' The check predates the last write, so it will refresh on the next verification pass.'
    : '';

  if (injectionStatus === 'failed') {
    // A withdrawn route's failure is a failed DELETE, not a failed write: saying
    // "write failed" on a tombstone reads as if we tried to install it.
    const verb = row.withdrawn ? 'remove this route from' : 'write this route to';
    return {
      state: 'failed',
      label: row.withdrawn ? 'Withdraw failed' : 'Write failed',
      variant: 'error',
      detail: `The reflector could not ${verb} AWS${
        injectionError ? `: ${injectionError}` : '.'
      }`,
      stale: false
    };
  }

  if (awsState === 'blackhole') {
    return {
      state: 'blackhole',
      label: 'Blackhole',
      variant: 'error',
      detail:
        'The route exists in AWS but its target is dead, so traffic is dropped. ' +
        `The reflector wrote it successfully at ${at(injectionAt)}.${checked}${staleNote}`,
      stale
    };
  }

  if (awsState === 'drift' || awsState === 'missing') {
    return {
      state: 'drift',
      label: awsState === 'missing' ? 'Missing' : 'Drift',
      variant: 'warning',
      detail:
        awsState === 'missing'
          ? `The route is not present in AWS even though the reflector wrote it at ${at(injectionAt)}.${checked}`
          : `The route in AWS points somewhere else than the reflector wrote at ${at(injectionAt)}.${checked}`,
      stale
    };
  }

  if (injectionStatus === 'withdrawn') {
    return {
      state: 'withdrawn',
      label: 'Withdrawn',
      variant: 'neutral',
      detail: `The reflector removed this route from AWS at ${at(injectionAt)}.`,
      stale: false
    };
  }

  if (awsState === 'active') {
    return {
      state: 'healthy',
      label: 'Active',
      variant: 'success',
      detail: `Written by the reflector and confirmed live in AWS.${checked}${staleNote}`,
      stale
    };
  }

  // Written, but no verdict yet (first pass pending) or the table could not be
  // read. Deliberately neutral: claiming health we have not verified is what
  // hid the original incident.
  return {
    state: 'unverified',
    label: 'Not verified',
    variant: 'neutral',
    detail:
      awsState === 'table_missing'
        ? 'The reflector wrote this route but could not read its route table back from AWS.'
        : `Written by the reflector at ${at(injectionAt)}; not verified in AWS yet.`,
    stale: false
  };
}

/**
 * Severity order, worst first. Exported so the table can sort by "how broken"
 * instead of alphabetically — the whole point of the column is that broken
 * routes surface first.
 */
export const CLOUD_HEALTH_SEVERITY: CloudRouteHealthState[] = [
  'failed',
  'blackhole',
  'drift',
  'withdrawn',
  'unverified',
  'healthy',
  'unknown'
];

/**
 * Worst state across a group of rows (the table groups by prefix, and a prefix
 * can span several route tables with different outcomes).
 *
 * Two rules, both about describing the prefix as it stands NOW:
 *  - Withdraw tombstones only speak when the whole group is withdrawn. A stale
 *    tombstone lingers next to a live announcement (they key on different route
 *    tables, so one never supersedes the other), and letting its failure win
 *    masked the live route's real state — observed in the lab, where a failed
 *    withdraw hid an actual blackhole on the same prefix.
 *  - `unknown` only wins when every row is unknown: one reporting row is enough
 *    to say something (e.g. the primary reports, the secondary never writes).
 */
export function worstCloudRouteHealth(rows: RouteRow[]): CloudRouteHealth {
  const order = CLOUD_HEALTH_SEVERITY;
  const live = rows.filter((r) => !r.withdrawn);
  const healths = (live.length > 0 ? live : rows).map(deriveCloudRouteHealth);
  const known = healths.filter((h) => h.state !== 'unknown');
  const pool = known.length > 0 ? known : healths;
  return pool.reduce((worst, h) =>
    order.indexOf(h.state) < order.indexOf(worst.state) ? h : worst
  );
}
